import React, { useState, useEffect, useRef, useCallback } from "react";
import chatBoxCss from "./ChatBoxes.module.css";
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Person4Icon from '@mui/icons-material/Person4';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector } from "react-redux";
import { formatLargeText, copyText } from "../../../utils/client-functions";
import { useErrorSetter, useSuccessSetter } from '../../../hooks/useUiMessages';
import styled, { keyframes, css } from "styled-components";
import { useTheme } from "../Theme/ThemeContext";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(62, 143, 147, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(62, 143, 147, 0); }
  100% { box-shadow: 0 0 0 0 rgba(62, 143, 147, 0); }
`;

const MessageWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 12px 16px;
  box-sizing: border-box;
  animation: ${fadeIn} 0.4s ease-out;
  justify-content: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  align-items: flex-end;
  gap: 12px;
`;

const BubbleBase = styled.div`
  max-width: 85%;
  padding: 14px 18px;
  border-radius: 20px;
  position: relative;
  font-size: 0.95rem;
  line-height: 1.6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
`;

const UserBubble = styled(BubbleBase)`
  background: linear-gradient(135deg, #3E8F93 0%, #2c6b6e 100%);
  color: white;
  border-bottom-right-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: -8px;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #2c6b6e 0%, transparent 100%);
    clip-path: polygon(0 0, 0% 100%, 100% 100%);
    z-index: -1;
    display: none; /* Modern clean design often skips the tail */
  }
`;

const AssistantBubble = styled(BubbleBase)`
  background: ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.8)'};
  color: ${props => props.$theme === 'dark' ? '#ffffff' : '#0C1E28'};
  backdrop-filter: blur(12px);
  border: 1px solid ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(62, 143, 147, 0.2)'};
  border-bottom-left-radius: 4px;

  ${props => props.$isTyping && css`
    border-color: rgba(62, 143, 147, 0.5);
    animation: ${pulse} 2s infinite;
  `}
`;

const AvatarWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => props.$theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${props => props.$theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
  color: #3E8F93;
  margin-bottom: 2px;
`;

const CopyButton = styled.div`
  opacity: 0;
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: inherit;
  transition: all 0.2s ease;
  z-index: 10;

  ${BubbleBase}:hover & {
    opacity: 0.7;
  }

  &:hover {
    opacity: 1 !important;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ScrollToBottomButton = styled.div`
  position: fixed;
  bottom: 200px;
  right: 20px;
  background: ${props => props.$theme === 'dark' ? 'rgba(62, 143, 147, 0.8)' : '#3E8F93'};
  color: white;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  ${props => props.$visible && css`
    opacity: 1;
    transform: translateY(0);
  `}

  &:hover {
    transform: scale(1.1);
    background: #3E8F93;
    box-shadow: 0 6px 20px rgba(62, 143, 147, 0.4);
  }
`;

// Simple component for the scroll-to-bottom button
export const ScrollToBottom = ({ onClick, visible }) => {
  const { themeName } = useTheme();
  return (
    <ScrollToBottomButton 
      $visible={visible}
      $theme={themeName}
      onClick={onClick}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 13L12 18L17 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7 6L12 11L17 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </ScrollToBottomButton>
  );
};

// User message component
export const User = (props) => {
  const { themeName } = useTheme();
  const errorSetter = useErrorSetter();
  const successSetter = useSuccessSetter();

  const handleCopy = () => {
    const textToCopy = typeof props.children === 'string' 
      ? props.children 
      : React.Children.map(props.children, child => 
          typeof child === "string" ? child : 
          React.isValidElement(child) ? child.props.children : ""
        ).join('');

    copyText(textToCopy)
      .then(() => successSetter('Text copied to clipboard'))
      .catch(err => {
        errorSetter('Failed to copy text: ' + (err?.message || 'Unknown error'));
        try { window.prompt('Copy this text:', textToCopy); } catch(_) {}
      });
  };


  return (
    <MessageWrapper $isUser={true}>
      <UserBubble $theme={themeName}>
        <div className={chatBoxCss.userContent}>
          {formatLargeText(props.children)}
        </div>
        <CopyButton onClick={handleCopy} title="Copy Message">
          <ContentCopyIcon fontSize="inherit" style={{ fontSize: '0.9rem' }} />
        </CopyButton>
      </UserBubble>
      <AvatarWrapper $theme={themeName}>
        <Person4Icon sx={{ fontSize: '1.2rem'}} />
      </AvatarWrapper>
    </MessageWrapper>
  );
};

// Assistant message component
export const Assistant = (props) => {
  const { themeName } = useTheme();
  const { messages } = useSelector((state) => state.stateData);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [typingComplete, setTypingComplete] = useState(false); // kept for logic flow
  useEffect(() => { /* no-op to satisfy linter for now */ }, [typingComplete]);
  const typingTimerRef = useRef(null);
  const messageRef = useRef(null);
  const messageContentRef = useRef(''); // Store message content for comparison
  const initialRenderRef = useRef(true); // Track initial render
  const messagesRef = useRef([]); // Store previous messages array for comparison
  
  const errorSetter = useErrorSetter();

  const successSetter = useSuccessSetter();

  // Function to scroll to the bottom of the chat
  const scrollToBottom = useCallback(() => {
    const chatContainer = document.querySelector('.chat-ex');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, []);

  // Extract the raw text content from props.children
  const getRawContent = useCallback(() => {
    if (props.contentTrim) return '';

    if (typeof props.children === 'string') {
      return props.children;
    } else if (Array.isArray(props.children)) {
      return React.Children.map(props.children, child => 
        typeof child === 'string' ? child : 
        React.isValidElement(child) && child.props.children ? 
          (typeof child.props.children === 'string' ? 
            child.props.children : '') : ''
      ).join('');
    } else if (React.isValidElement(props.children) && props.children.props.children) {
      return typeof props.children.props.children === 'string' ? 
        props.children.props.children : '';
    }
    return '';
  }, [props.children, props.contentTrim]);

  // Check if this is the latest assistant message
  const isLatestAssistantMessage = useCallback(() => {
    if (!messages || messages.length === 0) return false;

    // Get the last message in the array
    const lastMessage = messages[messages.length - 1];

    // If the last message is from the assistant, check if it matches this component
    if (lastMessage.role === 'assistant') {
      const content = getRawContent();
      return lastMessage.content === content;
    }

    return false;
  }, [messages, getRawContent]);

  // Track message array changes
  useEffect(() => {
    // Update the reference when messages array changes
    if (messages !== messagesRef.current) {
      messagesRef.current = messages;
    }
  }, [messages]);

  // Format code blocks in the message
  const formatCodeBlocks = (text) => {
    if (!text) return [];
    
    const codeBlockRegex = /```([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = codeBlockRegex.exec(text)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Extract code and language if specified
      const codeContent = match[1];
      let language = '';
      let code = codeContent;
      
      // Check if the first line specifies a language
      const firstLineMatch = codeContent.match(/^(\w+)\s*\n([\s\S]*)$/);
      if (firstLineMatch) {
        language = firstLineMatch[1];
        code = firstLineMatch[2];
      }
      
      // Preserve indentation by not trimming
      parts.push({
        type: 'code',
        language,
        content: code
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts;
  };

  // Handle copying code blocks
  const handleCodeCopy = (codeText) => {
    copyText(codeText)
      .then(() => successSetter('Code copied to clipboard'))
      .catch(err => {
        errorSetter('Failed to copy code: ' + (err?.message || 'Unknown error'));
        try { window.prompt('Copy this code:', codeText); } catch(_) {}
      });
  };

  // Handle copying the entire message
  const handleCopy = () => {
    const textToCopy = getRawContent();
    copyText(textToCopy)
      .then(() => successSetter('Text copied to clipboard'))
      .catch(err => {
        errorSetter('Failed to copy text: ' + (err?.message || 'Unknown error'));
        try { window.prompt('Copy this text:', textToCopy); } catch(_) {}
      });
  };

 
  // Format text with markdown-like syntax (bold, italic, links, h1 headings)
  
  const formatText = (text) => {
    if (!text) return text;

    const BUBBLE_AI_ORIGIN = 'https://www.bubble-ai.tech';

    const normalizeHref = (href) => {
      const value = String(href || '').trim();
      if (!value) return null;
      if (value.startsWith('/')) return `${BUBBLE_AI_ORIGIN}${value}`;
      if (/^https?:\/\//i.test(value)) return value;
      return null;
    };

    const linkAttributes = (href) =>
      `href="${href}" target="_blank" rel="noopener noreferrer"`;

    // Helper: convert markdown-style links: [label](https://...) or [label](/path)
    const convertMarkdownLinks = (str) => {
      return str.replace(/\[([^\]]+)\]\(((https?:\/\/|\/)[^\s)]+)\)/g, (m, label, url) => {
        const normalizedHref = normalizeHref(url);
        return normalizedHref
          ? `<a ${linkAttributes(normalizedHref)}>${label}</a>`
          : label;
      });
    };

    // Helper: convert plain internal paths like /user/dashboard/mock to links
    const convertRelativeUrls = (str) => {
      const parts = str.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi);
      return parts.map(part => {
        if (/^<a\b/i.test(part)) return part;
        return part.replace(/(?<!['"=\w])(\/(?:user|chat|school|popin|join-bubble|mock)(?:\/[^\s,;.!?)"'<>*_\\]*)?)/g, (match) => {
          const normalizedHref = normalizeHref(match);
          return `<a ${linkAttributes(normalizedHref)}>Click here</a>`;
        });
      }).join('');
    };

    // Helper: convert plain URLs to anchors but avoid replacing URLs that are already inside <a> tags
    // We split the string on existing anchor tags and only convert URLs in the non-anchor parts.
    const convertPlainUrls = (str) => {
      // Split by existing <a ...>...</a> tags (case-insensitive)
      const parts = str.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi);

      return parts.map(part => {
        // If this part is an anchor tag, leave it unchanged
        if (/^<a\b/i.test(part)) return part;

        // Otherwise replace plain URLs in this part
        return part.replace(/(https?:\/\/[^\s)]+)/g, (match) => {
          let url = match;
          let trailing = '';

          // Trim trailing punctuation that commonly follows URLs in text (e.g. ')', '.', ',', ']', '"')
          while (url.length && /[)\].,!?;:'"]/.test(url[url.length - 1])) {
            trailing = url[url.length - 1] + trailing;
            url = url.slice(0, -1);
          }

          return `<a ${linkAttributes(url)}>Click here</a>` + trailing;
        });
      }).join('');
    };

    // Normalize anchors supplied directly by AskMe, while dropping unsafe schemes.
    const normalizeExistingAnchors = (str) => {
      return str.replace(/<a\b([^>]*)\bhref\s*=\s*["']([^"']+)["']([^>]*)>([\s\S]*?)<\/a>/gi, (match, before, href, after, label) => {
        const normalizedHref = normalizeHref(href);
        return normalizedHref
          ? `<a ${linkAttributes(normalizedHref)}>${label}</a>`
          : label;
      });
    };

    // First convert markdown links, then relative paths, then plain absolute URLs
    const processed = normalizeExistingAnchors(convertPlainUrls(convertRelativeUrls(convertMarkdownLinks(text))));

    // Split text by double newlines to identify paragraphs
    const paragraphs = processed.split(/\n\s*\n/);

    return paragraphs.map((paragraph, pIndex) => {
      // Split paragraph by single newlines to handle line breaks within paragraphs
      const lines = paragraph.split(/\n/);

      return (
        <div key={`p-${pIndex}`} className={chatBoxCss.paragraph}>
          {lines.map((line, lIndex) => {
            if (!line.trim()) {
              return <br key={`br-${pIndex}-${lIndex}`} />;
            }

            // Check if line starts with ### for h1 heading
            const isH1Heading = line.trim().startsWith('###') || line.trim().startsWith('####');
            let processedLine = isH1Heading ? line.trim().substring(3).trim() : line;

            // Preserve indentation (only if not a heading)
            const indentMatch = !isH1Heading ? processedLine.match(/^(\s+)/) : null;
            const indent = indentMatch ? indentMatch[0].length : 0;
            const indentStyle = indent > 0 ? { paddingLeft: `${indent * 0.5}em` } : {};

            // Format bold text
            let formattedLine = processedLine.replace(
              /\*\*(.*?)\*\*|__(.*?)__/g,
              (match, p1, p2) => `<strong>${p1 || p2}</strong>`
            );

            // Format italic text
            formattedLine = formattedLine.replace(
              /\*(.*?)\*|_(.*?)_/g,
              (match, p1, p2) => `<em>${p1 || p2}</em>`
            );

            // At this point links were already converted in `processed` (so leave them as-is)

            // Apply H1 styling or regular styling
            if (isH1Heading) {
              return (
                <h1 
                  key={`heading-${pIndex}-${lIndex}`}
                  className={chatBoxCss.textLine}
                  dangerouslySetInnerHTML={{ __html: formattedLine }}
                  style={{ fontWeight: 'bold', fontSize: '2em', margin: '0.3em 0' }}
                />
              );
            } else {
              return (
                <div 
                  key={`line-${pIndex}-${lIndex}`}
                  style={indentStyle}
                  className={chatBoxCss.textLine}
                  dangerouslySetInnerHTML={{ __html: formattedLine }}
                />
              );
            }
          })}
        </div>
      );
    });
  };

  // Start typing animation
  const startTypingAnimation = useCallback((text) => {
    setIsTyping(true);
    setTypingComplete(false);
    setDisplayedText('');

    let i = 0;
    const textLength = text.length;
    // Slower typing speed (higher values = slower typing)
    const typingSpeed = Math.max(20, Math.min(40, 1200 / textLength));

    // Clear any existing timer
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }

    // Use a faster initial typing speed for the first few characters
    // to make the animation feel more responsive
    const initialChunkSize = Math.min(20, Math.floor(textLength * 0.05));
    if (initialChunkSize > 0) {
      setDisplayedText(text.substring(0, initialChunkSize));
      i = initialChunkSize;
      scrollToBottom();
    }

    typingTimerRef.current = setInterval(() => {
      if (i < textLength) {
        // Add fewer characters at once for slower typing
        const chunkSize = Math.max(1, Math.floor(textLength / 400));
        const nextChunk = text.substring(i, Math.min(i + chunkSize, textLength));
        setDisplayedText(prev => prev + nextChunk);
        i += nextChunk.length;

        // Scroll to bottom on each update to follow the typing
        scrollToBottom();

        // Scroll more frequently when new lines are added
        if (nextChunk.includes('\n')) {
          setTimeout(scrollToBottom, 50);
        }
      } else {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        setIsTyping(false);
        setTypingComplete(true);
        scrollToBottom();
      }
    }, typingSpeed);
  }, [scrollToBottom]);

  // Effect to handle typing animation
  useEffect(() => {
    // Skip animation for empty content or loading indicator
    if (props.contentTrim || !props.children) {
      setIsTyping(false);
      setDisplayedText('');
      return;
    }
    
    const content = getRawContent();
    
    // Check if this is the latest assistant message
    const isLatest = isLatestAssistantMessage();
    
    // Check if content has changed (new message)
    const isNewContent = content !== messageContentRef.current;
    
    // Update the reference
    const prevContent = messageContentRef.current;
    messageContentRef.current = content;
    
    // On initial render, just display the content without animation
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      setIsTyping(false);
      setDisplayedText(content);
      setTypingComplete(true);
    }
    // Animate only if this is the latest message and it's new content (not empty)
    // This ensures animation only happens for new responses to user queries
    else if (isLatest && isNewContent && content.trim() !== '' && prevContent !== content) {
      startTypingAnimation(content);
    } else {
      setIsTyping(false);
      setDisplayedText(content);
    }
    
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };
  }, [props.children, props.contentTrim, messages, getRawContent, isLatestAssistantMessage, startTypingAnimation]);

  // Scroll to bottom when component mounts
  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  // Render the message content
  const renderContent = () => {
    if (props.contentTrim) return props.children;
    
    if (isTyping) {
      // During typing animation, just format the partial text
      return formatText(displayedText);
    } else {
      // After typing is complete, format with code blocks
      const content = getRawContent();
      const parts = formatCodeBlocks(content);
      
      return parts.map((part, index) => {
        if (part.type === 'text') {
          return (
            <div key={`text-${index}`} className={chatBoxCss.textContent}>
              {formatText(part.content)}
            </div>
          );
        } else if (part.type === 'code') {
          return (
            <div key={`code-${index}`} className={chatBoxCss.codeBlock}>
              {part.language && (
                <div className={chatBoxCss.codeLanguage}>{part.language}</div>
              )}
              <pre className={chatBoxCss.codeContent}>
                {part.content}
              </pre>
              <span 
                className={chatBoxCss.codeCopyIcon} 
                onClick={() => handleCodeCopy(part.content)}
              >
                <ContentCopyIcon fontSize="small" />
              </span>
            </div>
          );
        }
        return null;
      });
    }
  };

  return (
    <MessageWrapper $isUser={false}>
      <AvatarWrapper $theme={themeName}>
        <SmartToyIcon sx={{ fontSize: '1.2rem'}} />
      </AvatarWrapper>
      <AssistantBubble $theme={themeName} $isTyping={isTyping} ref={messageRef}>
        
        <div className={chatBoxCss.assistantContent}>
          {renderContent()}
        </div>

        {!props.contentTrim && (
          <CopyButton onClick={handleCopy} title="Copy Message">
            <ContentCopyIcon fontSize="inherit" style={{ fontSize: '0.9rem' }} />
          </CopyButton>
        )}
      </AssistantBubble>
    </MessageWrapper>
  );
};
